import * as THREE from 'three';

const isMobile = window.innerWidth <= 768;

// ===== UAE FLAG POSITION CONTROLS =====
const FLAG_BASE_X = isMobile ? 350  : 380;
const FLAG_BASE_Y = isMobile ? -100 : -100;
const FLAG_BASE_Z = isMobile ? 100  : 100;
// ======================================

// ===== POLE TILT CONTROLS =====
const POLE_TILT_ANGLE = isMobile ? 10 : 10;
const TILT_DIRECTION  = isMobile ? 90 : 90;
// ==============================

// ===== POLE MATERIAL CONTROLS =====
const POLE_COLOR             = 0xd7cfc8;
const POLE_METALNESS         = 1;
const POLE_ROUGHNESS         = 0.5;
const POLE_EMISSIVE          = 0xd7cfc8;
const POLE_EMISSIVE_INTENSITY = 0.1;
// ==================================

// ===== FLAG TEXTURE TINT =====
const FLAG_TINT_COLOR = 0xffffff;
// =============================

// ===== FLAG SIDE =====
const FLAG_ON_RIGHT_SIDE = true;
// =====================

const FLAG_WIDTH  = isMobile ? 76  : 76;
const FLAG_HEIGHT = isMobile ? 52  : 52;
const POLE_HEIGHT = isMobile ? 240 : 240;
const POLE_RADIUS = isMobile ? 2.0 :2.2;

// Wave vertex shader
const WAVE_VERT = `
uniform float uTime;
varying vec2  vUv;
void main(){
  vec3 p = position;
  float u = (p.x + ${FLAG_WIDTH / 2}.) / ${FLAG_WIDTH}.;
  p.z = sin(u * 3.14159 * 2.5 + uTime * 2.8) * 5.2 * u;
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.);
}
`;

// Fragment shader with tint support
const WAVE_FRAG = `
uniform sampler2D uTex;
uniform vec3 uTint;
varying vec2 vUv;
void main(){
  vec4 texColor = texture2D(uTex, vUv);
  gl_FragColor = vec4(texColor.rgb * uTint, texColor.a);
}
`;

export class UAEFlag {
  constructor(scene) {
    this.scene     = scene;
    this._flagMat  = null;
    this._flagMesh = null;
    this._group    = null;
  }

  init() {
    const baseX = FLAG_BASE_X;
    const baseY = FLAG_BASE_Y;
    const baseZ = FLAG_BASE_Z;

    const tiltRad = THREE.MathUtils.degToRad(POLE_TILT_ANGLE);
    const dirRad  = THREE.MathUtils.degToRad(TILT_DIRECTION);

    const axis = new THREE.Vector3(
      -Math.sin(dirRad), 0, Math.cos(dirRad)
    ).normalize();

    const group = new THREE.Group();
    group.position.set(baseX, baseY, baseZ);
    group.rotateOnWorldAxis(axis, tiltRad);

    const poleMat = new THREE.MeshStandardMaterial({
      color:             POLE_COLOR,
      metalness:         POLE_METALNESS,
      roughness:         POLE_ROUGHNESS,
      emissive:          POLE_EMISSIVE,
      emissiveIntensity: POLE_EMISSIVE_INTENSITY,
    });

    // Pole
    const poleGeo = new THREE.CylinderGeometry(
      POLE_RADIUS, POLE_RADIUS * 1.3, POLE_HEIGHT, 18
    );
    const pole = new THREE.Mesh(poleGeo, poleMat);
    pole.position.set(0, POLE_HEIGHT / 2, 0);
    pole.userData.noBloom = true;
    group.add(pole);

    // Tip sphere
    const tipGeo = new THREE.SphereGeometry(3.5, 18, 18);
    const tip = new THREE.Mesh(tipGeo, poleMat.clone());
    tip.position.set(0, POLE_HEIGHT, 0);
    tip.userData.noBloom = true;
    group.add(tip);

    // Flag cloth
    const flagGeo = new THREE.PlaneGeometry(FLAG_WIDTH, FLAG_HEIGHT, 36, 8);
    this._flagMat = new THREE.ShaderMaterial({
      vertexShader:   WAVE_VERT,
      fragmentShader: WAVE_FRAG,
      uniforms: {
        uTime: { value: 0 },
        uTex:  { value: null },
        uTint: { value: new THREE.Color(FLAG_TINT_COLOR) },
      },
      transparent: false,
      side: THREE.DoubleSide,
    });

    this._flagMesh = new THREE.Mesh(flagGeo, this._flagMat);

    const flagOffsetX = FLAG_ON_RIGHT_SIDE
      ? FLAG_WIDTH / 2
      : -FLAG_WIDTH / 2;

    this._flagMesh.position.set(
      flagOffsetX,
      POLE_HEIGHT - FLAG_HEIGHT / 2,
      0
    );
    this._flagMesh.userData.noBloom = true;
    group.add(this._flagMesh);

    this.scene.add(group);
    this._group = group;

    // Load UAE flag texture
    const loader = new THREE.TextureLoader();
    loader.load(
      'https://flagcdn.com/w80/ae.png',
      tex => { this._flagMat.uniforms.uTex.value = tex; },
    );
  }

  update(time) {
    if (this._flagMat) this._flagMat.uniforms.uTime.value = time;
  }
}